/**
 * Temporal.tsx — Market Intelligence page.
 *
 * Rebuilt in final pass.
 *
 * Preserves all behavior:
 *   - Load available cycle dates + closures (/api/temporal/cycles, /api/temporal/closures)
 *   - Compare any two cycles (/api/temporal/diff?current=&previous=)
 *   - Summary KPIs: new outlets / disappeared / probable closures / net change
 *   - Two side-by-side tables: New this cycle / Disappeared
 *   - Per-client coverage-change table below
 *   - Admin-only source-count popup on each outlet row
 *   - Empty state when fewer than 2 cycles exist
 *
 * Changes:
 *   - Uses shared UI (Card, Tag, Button, Select, StatCard, Icon, Table, toast, Skeleton)
 *   - Source popup is now a modal-ish overlay inside a Card instead of a bare div
 *   - Census IDs are rendered as clickable cyan links (was: cyan text)
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { theme } from '../../theme';
import { useAdmin } from '../../AdminContext';
import {
  Card,
  Tag,
  Select,
  StatCard, StatGrid,
  Icon,
  Table, type Column,
  EmptyState,
  Skeleton,
  MethodTag, ConfidenceTag,
  IconButton,
} from '../../components/ui';

/* ═══════════════ TYPES ═══════════════ */

interface CycleDate {
  cycle_date: string;
  outlet_count: number;
  client_count: number;
}

interface DiffOutlet {
  census_id: string;
  name: string;
  city: string;
  seen_by?: number;
  was_seen_by?: number;
}

interface ClientChange {
  client_name: string;
  current_count: number;
  previous_count: number;
  gained: number;
  lost: number;
}

interface CycleDiff {
  current_cycle: string;
  previous_cycle: string;
  current_total_outlets: number;
  previous_total_outlets: number;
  new_outlets_count: number;
  disappeared_count: number;
  new_outlets: DiffOutlet[];
  disappeared: DiffOutlet[];
  per_client: ClientChange[];
}

interface ClosureItem {
  census_id: string;
  name: string;
  city: string;
  last_seen: string;
  cycles_seen: number;
  clients_seen: number;
}

interface SourceRecord {
  client_name: string;
  raw_name?: string;
  match_confidence?: number | null;
  match_method?: string;
}

/* ═══════════════ PAGE ═══════════════ */

export default function Temporal() {
  const navigate = useNavigate();
  const { adminMode, maskClientName, viewAsClient, modeVersion } = useAdmin();

  const [cycles, setCycles] = useState<CycleDate[]>([]);
  const [diff, setDiff] = useState<CycleDiff | null>(null);
  const [closures, setClosures] = useState<ClosureItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [diffLoading, setDiffLoading] = useState(false);
  const [currentCycle, setCurrentCycle] = useState('');
  const [previousCycle, setPreviousCycle] = useState('');

  // Sources popup (admin only)
  const [popupCensusId, setPopupCensusId] = useState<string | null>(null);
  const [popupSources, setPopupSources] = useState<SourceRecord[]>([]);
  const [popupLoading, setPopupLoading] = useState(false);
  const popupRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.title = 'Market Intelligence | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  // Close popup on outside click
  useEffect(() => {
    if (!popupCensusId) return;
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setPopupCensusId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popupCensusId]);

  const handleSourceClick = async (censusId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (popupCensusId === censusId) { setPopupCensusId(null); return; }
    setPopupCensusId(censusId);
    if (adminMode) {
      setPopupLoading(true);
      setPopupSources([]);
      try {
        const res = await api.get(`/api/registry/outlet/${censusId}`);
        setPopupSources(res.data.clients || []);
      } catch {
        setPopupSources([]);
      } finally {
        setPopupLoading(false);
      }
    }
  };

  // Initial load
  useEffect(() => {
    async function load() {
      try {
        const [cycleRes, closureRes] = await Promise.all([
          api.get<CycleDate[]>('/api/temporal/cycles'),
          api.get<ClosureItem[]>('/api/temporal/closures'),
        ]);
        setCycles(cycleRes.data);
        setClosures(closureRes.data);
        if (cycleRes.data.length >= 2) {
          setCurrentCycle(cycleRes.data[0].cycle_date);
          setPreviousCycle(cycleRes.data[1].cycle_date);
        } else if (cycleRes.data.length === 1) {
          setCurrentCycle(cycleRes.data[0].cycle_date);
        }
      } catch {
        // handled by empty state
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [modeVersion]);

  // Load diff whenever cycle selection changes
  const loadDiff = useCallback(async () => {
    if (!currentCycle || !previousCycle) return;
    setDiffLoading(true);
    try {
      const res = await api.get<CycleDiff>('/api/temporal/diff', {
        params: { current: currentCycle, previous: previousCycle },
      });
      setDiff(res.data);
    } catch {
      setDiff(null);
    } finally {
      setDiffLoading(false);
    }
  }, [currentCycle, previousCycle]);

  useEffect(() => { loadDiff(); }, [loadDiff]);

  if (loading) {
    return (
      <div style={{ padding: 32 }}>
        <Skeleton width={200} height={28} />
        <div style={{ marginTop: 12 }}><Skeleton width={300} height={14} /></div>
        <div style={{ marginTop: 32, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {[0, 1, 2, 3].map(i => <Card key={i}><Skeleton width={80} height={12} /><div style={{ marginTop: 10 }}><Skeleton width={60} height={24} /></div></Card>)}
        </div>
      </div>
    );
  }

  const hasCycles = cycles.length >= 2;

  return (
    <div style={{ padding: 32 }}>
      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 600,
          color: theme.text.primary, margin: 0,
          letterSpacing: '-0.02em',
        }}>Market intelligence</h1>
        <div aria-hidden style={{
          height: 3, width: 40,
          background: theme.brand.cyan,
          borderRadius: 2,
          boxShadow: `0 0 12px ${theme.brand.cyanGlow}`,
          marginTop: 6,
        }} />
        <div style={{
          fontSize: 11, fontWeight: 600,
          color: theme.text.tertiary,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          marginTop: 8,
        }}>Track how the retail landscape changes over time</div>
      </div>

      {!hasCycles && (
        <Card>
          <EmptyState
            icon={<Icon.Clock size={28} />}
            title={
              cycles.length === 0
                ? 'No data submission cycles recorded yet'
                : 'One data submission cycle recorded'
            }
            description={
              cycles.length === 0
                ? 'Market Intelligence populates after your first complete data submission. Submit your outlet data to start tracking market changes.'
                : `Market Intelligence requires at least two data submission cycles. Your first cycle was recorded on ${cycles[0].cycle_date} with ${cycles[0].outlet_count.toLocaleString()} outlets. Submit updated data to unlock trend analysis.`
            }
          />
        </Card>
      )}

      {hasCycles && (
        <>
          <div style={{
            fontSize: 13,
            color: theme.text.secondary,
            marginBottom: 14,
            lineHeight: 1.5,
          }}>
            Each cycle represents a data submission. Compare cycles to track outlet changes over time.
          </div>

          {/* Cycle selectors */}
          <div style={{
            display: 'flex', gap: 16, alignItems: 'flex-end',
            marginBottom: 28,
            flexWrap: 'wrap',
          }}>
            <CycleSelector
              label="Current cycle"
              value={currentCycle}
              options={cycles}
              onChange={setCurrentCycle}
            />
            <div style={{
              color: theme.text.tertiary,
              fontSize: 18,
              fontWeight: 600,
              paddingBottom: 8,
            }}>vs</div>
            <CycleSelector
              label="Previous cycle"
              value={previousCycle}
              options={cycles}
              onChange={setPreviousCycle}
            />
          </div>

          {diffLoading && (
            <Card>
              <div style={{ padding: 12 }}>
                <Skeleton width="60%" height={14} />
              </div>
            </Card>
          )}

          {diff && !diffLoading && (
            <>
              {/* Summary KPIs */}
              <StatGrid columns={4}>
                <StatCard
                  label="New outlets"
                  value={diff.new_outlets_count.toLocaleString()}
                  icon={<Icon.Plus size={14} />}
                />
                <StatCard
                  label="Disappeared"
                  value={diff.disappeared_count.toLocaleString()}
                  icon={<Icon.Minus size={14} />}
                />
                <StatCard
                  label="Probable closures"
                  value={closures.length.toLocaleString()}
                  icon={<Icon.AlertTriangle size={14} />}
                />
                <StatCard
                  primary
                  label="Net change"
                  value={
                    (diff.current_total_outlets - diff.previous_total_outlets >= 0 ? '+' : '') +
                    (diff.current_total_outlets - diff.previous_total_outlets).toLocaleString()
                  }
                  deltaType={
                    diff.current_total_outlets >= diff.previous_total_outlets ? 'up' : 'down'
                  }
                />
              </StatGrid>

              {/* New + Disappeared tables */}
              <div style={{ marginTop: 28 }}
                className="meridian-temporal-grid">
                <style>{`
                  @media (min-width: 1024px) {
                    .meridian-temporal-grid {
                      display: grid !important;
                      grid-template-columns: 1fr 1fr !important;
                      gap: 20px !important;
                    }
                  }
                `}</style>

                <div style={{ marginBottom: 20 }}>
                  <SectionLabel>New this cycle</SectionLabel>
                  <OutletDiffTable
                    outlets={diff.new_outlets}
                    emptyLabel="No new outlets"
                    countLabelFn={(o) => o.seen_by}
                    showSources={adminMode && !viewAsClient}
                    onSourceClick={handleSourceClick}
                    popupCensusId={popupCensusId}
                    popupSources={popupSources}
                    popupLoading={popupLoading}
                    popupRef={popupRef}
                    navigate={navigate}
                    maskClientName={maskClientName}
                    badgeKind="success"
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <SectionLabel>Disappeared</SectionLabel>
                  <OutletDiffTable
                    outlets={diff.disappeared}
                    emptyLabel="No disappeared outlets"
                    countLabelFn={(o) => o.was_seen_by}
                    showSources={adminMode && !viewAsClient}
                    onSourceClick={handleSourceClick}
                    popupCensusId={popupCensusId}
                    popupSources={popupSources}
                    popupLoading={popupLoading}
                    popupRef={popupRef}
                    navigate={navigate}
                    maskClientName={maskClientName}
                    badgeKind="danger"
                  />
                </div>
              </div>

              {/* Per-client coverage */}
              {diff.per_client.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <SectionLabel>Coverage changes by client</SectionLabel>
                  <Card padding="none">
                    <Table
                      columns={perClientColumns(maskClientName)}
                      data={diff.per_client}
                      rowKey={r => r.client_name}
                      compact
                    />
                  </Card>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ═══════════════ SUB-COMPONENTS ═══════════════ */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 style={{
      fontSize: 11, fontWeight: 600,
      color: theme.brand.cyan,
      textTransform: 'uppercase',
      letterSpacing: '0.16em',
      margin: '0 0 12px 0',
    }}>{children}</h3>
  );
}

function CycleSelector({
  label, value, options, onChange,
}: {
  label: string;
  value: string;
  options: CycleDate[];
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <div style={{
        fontSize: 11, fontWeight: 600,
        color: theme.text.tertiary,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        marginBottom: 6,
      }}>{label}</div>
      <div style={{ minWidth: 320 }}>
        <Select value={value} onChange={e => onChange(e.target.value)}>
          {options.map(c => (
            <option key={c.cycle_date} value={c.cycle_date}>
              {c.cycle_date} · {c.outlet_count.toLocaleString()} outlets · {c.client_count} clients
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}

/* ─── Outlet-diff table (new or disappeared) ─── */

function OutletDiffTable({
  outlets, emptyLabel, countLabelFn, showSources,
  onSourceClick, popupCensusId, popupSources, popupLoading, popupRef,
  navigate, maskClientName, badgeKind,
}: {
  outlets: DiffOutlet[];
  emptyLabel: string;
  countLabelFn: (o: DiffOutlet) => number | undefined;
  showSources: boolean;
  onSourceClick: (censusId: string, e: React.MouseEvent) => void;
  popupCensusId: string | null;
  popupSources: SourceRecord[];
  popupLoading: boolean;
  popupRef: React.MutableRefObject<HTMLDivElement | null>;
  navigate: (path: string) => void;
  maskClientName: (name: string, index?: number) => string;
  badgeKind: 'success' | 'danger';
}) {
  if (outlets.length === 0) {
    return (
      <Card>
        <div style={{
          color: theme.text.tertiary,
          fontSize: 13,
          padding: 4,
        }}>{emptyLabel}</div>
      </Card>
    );
  }

  const shown = outlets.slice(0, 20);
  const extra = outlets.length - shown.length;

  return (
    <Card padding="none">
      <div style={{ overflow: 'visible' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13,
        }}>
          <thead>
            <tr style={{ background: theme.surface.card }}>
              <th style={thStyle}>Census ID</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>City</th>
              {showSources && <th style={{ ...thStyle, textAlign: 'center', width: 90 }}>Sources</th>}
            </tr>
          </thead>
          <tbody>
            {shown.map((o, i) => {
              const popupOpen = popupCensusId === o.census_id;
              return (
                <tr key={o.census_id} style={{
                  background: i % 2 === 0 ? theme.surface.panel : theme.surface.card,
                }}>
                  <td style={{ ...tdStyle, fontFamily: theme.font.data, fontSize: 12 }}>
                    <span
                      onClick={() => navigate(`/outlet/${o.census_id}`)}
                      style={{
                        color: theme.brand.cyanLight,
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                    >{o.census_id}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ color: theme.text.primary }}>{o.name || '—'}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ color: theme.text.secondary }}>{o.city || '—'}</span>
                  </td>
                  {showSources && (
                    <td style={{ ...tdStyle, textAlign: 'center', position: 'relative' }}>
                      <button
                        onClick={(e) => onSourceClick(o.census_id, e)}
                        style={{
                          display: 'inline-flex', alignItems: 'center',
                          padding: '2px 8px',
                          borderRadius: 4,
                          background: popupOpen ? theme.brand.cyanSoft : theme.surface.inset,
                          border: `1px solid ${popupOpen ? theme.brand.cyan : theme.borderColor.default}`,
                          color: theme.brand.cyanLight,
                          fontFamily: theme.font.data,
                          fontSize: 11, fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 120ms ease',
                        }}
                      >{countLabelFn(o)}</button>
                      {popupOpen && (
                        <div
                          ref={popupRef}
                          onClick={e => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 4px)',
                            right: 0,
                            zIndex: 50,
                            minWidth: 440,
                            background: theme.surface.elevated,
                            border: `1px solid ${theme.borderColor.brand}`,
                            borderRadius: 10,
                            boxShadow: theme.shadow.elevated,
                            padding: 14,
                            textAlign: 'left',
                          }}>
                          <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            marginBottom: 10,
                          }}>
                            <div style={{
                              fontSize: 10, fontWeight: 600,
                              color: theme.text.tertiary,
                              letterSpacing: '0.1em',
                              textTransform: 'uppercase',
                            }}>
                              Source records ({popupSources.length})
                            </div>
                            <IconButton
                              icon={<Icon.X size={12} />}
                              size="sm"
                              onClick={() => onSourceClick(o.census_id, { stopPropagation: () => {} } as React.MouseEvent)}
                              aria-label="Close"
                            />
                          </div>
                          {popupLoading ? (
                            <div style={{
                              color: theme.text.secondary,
                              fontSize: 13, padding: 6,
                            }}>Loading…</div>
                          ) : popupSources.length === 0 ? (
                            <div style={{
                              color: theme.text.tertiary,
                              fontSize: 12, padding: 4,
                            }}>No source records found.</div>
                          ) : (
                            <div style={{
                              display: 'grid',
                              gap: 6,
                              fontSize: 12,
                            }}>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1.5fr 1.5fr 80px 100px',
                                gap: 10,
                                paddingBottom: 6,
                                borderBottom: `1px solid ${theme.borderColor.subtle}`,
                                fontSize: 10, fontWeight: 600,
                                color: theme.text.tertiary,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                              }}>
                                <div>Client</div>
                                <div>Raw name</div>
                                <div style={{ textAlign: 'right' }}>Confidence</div>
                                <div>Method</div>
                              </div>
                              {popupSources.map((s, si) => (
                                <div key={si} style={{
                                  display: 'grid',
                                  gridTemplateColumns: '1.5fr 1.5fr 80px 100px',
                                  gap: 10,
                                  fontSize: 11,
                                  alignItems: 'center',
                                }}>
                                  <div style={{ color: theme.text.primary }}>
                                    {maskClientName(s.client_name)}
                                  </div>
                                  <div style={{ color: theme.text.tertiary }}>
                                    {s.raw_name || '—'}
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    {s.match_confidence != null
                                      ? <ConfidenceTag score={s.match_confidence} />
                                      : <span style={{ color: theme.text.tertiary }}>—</span>}
                                  </div>
                                  <div>
                                    <MethodTag method={s.match_method || ''} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {extra > 0 && (
        <div style={{
          padding: '10px 14px',
          background: theme.surface.card,
          borderTop: `1px solid ${theme.borderColor.subtle}`,
          fontSize: 12,
          color: theme.text.tertiary,
        }}>
          <Tag kind={badgeKind} size="sm">+{extra} more</Tag>
        </div>
      )}
    </Card>
  );
}

/* ─── Per-client coverage table columns ─── */

function perClientColumns(maskClientName: (n: string) => string): Column<ClientChange>[] {
  return [
    {
      key: 'client_name',
      header: 'Client',
      render: r => <span style={{ color: theme.text.primary, fontWeight: 500 }}>{maskClientName(r.client_name)}</span>,
    },
    {
      key: 'previous_count',
      header: 'Previous',
      mono: true,
      align: 'right',
      render: r => (
        <span style={{
          color: theme.text.secondary,
          fontVariantNumeric: 'tabular-nums',
        }}>{r.previous_count.toLocaleString()}</span>
      ),
    },
    {
      key: 'current_count',
      header: 'Current',
      mono: true,
      align: 'right',
      render: r => (
        <span style={{
          color: theme.text.primary,
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
        }}>{r.current_count.toLocaleString()}</span>
      ),
    },
    {
      key: 'gained',
      header: 'Gained',
      mono: true,
      align: 'right',
      render: r => (
        <span style={{
          color: r.gained > 0 ? theme.semantic.success : theme.text.tertiary,
          fontVariantNumeric: 'tabular-nums',
        }}>{r.gained > 0 ? `+${r.gained}` : '0'}</span>
      ),
    },
    {
      key: 'lost',
      header: 'Lost',
      mono: true,
      align: 'right',
      render: r => (
        <span style={{
          color: r.lost > 0 ? theme.semantic.danger : theme.text.tertiary,
          fontVariantNumeric: 'tabular-nums',
        }}>{r.lost > 0 ? `-${r.lost}` : '0'}</span>
      ),
    },
    {
      key: 'net',
      header: 'Net',
      mono: true,
      align: 'right',
      render: r => {
        const net = r.gained - r.lost;
        return (
          <span style={{
            color: net > 0 ? theme.semantic.success : net < 0 ? theme.semantic.danger : theme.text.tertiary,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}>{net > 0 ? `+${net}` : String(net)}</span>
        );
      },
    },
  ];
}

/* ═══════════════ STYLES ═══════════════ */

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 14px',
  color: theme.text.tertiary,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  borderBottom: `1px solid ${theme.borderColor.default}`,
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderBottom: `1px solid ${theme.borderColor.subtle}`,
  verticalAlign: 'middle',
};
